import LatLngLiteral = ym.provider.LatLngLiteral;
import { isArray } from 'util';
import { Address } from '../interfaces/generic/Address';
import { Offset } from '../interfaces/Offset';
import {
  RouteEntry,
  RouteGeocoderEntry,
  WayPoint
} from '../interfaces/route/RouteEntry';
import { RouteTargetEntry } from '../interfaces/route/RouteTargetEntry';
import { ClusterEntry } from '../interfaces/search/ClusterEntry';
import { GeocoderEntry } from '../interfaces/search/GeocoderEntry';
import { ResultEntry } from '../interfaces/search/ResultEntry';
import { SearchEntries } from '../interfaces/search/SearchEntries';
import {
  AddressStation,
  LocationStation,
  PositionStation,
  RoutingRequestData
} from '../interfaces/state-events/RoutingRequest';
import { UrlParameters } from '../interfaces/UrlParameters';

export function isNotEmptyString(obj: any): obj is string {
  return obj && 'string' === typeof obj && obj.trim().length > 0;
}

export function isValidTimeFormat(obj?: string): obj is string {
  return isNotEmptyString(obj) && /^\d{2}:\d{2}:\d{2}?$/.test(obj);
}

export function isNotNull<T>(obj: any): obj is T {
  return 'object' === typeof obj && obj !== null;
}

export function isAddressStation(obj: any): obj is AddressStation {
  return isNotNull<AddressStation>(obj) && obj.type === 'Address';
}

export function isLocationStation(obj: any): obj is LocationStation {
  return isNotNull<LocationStation>(obj) && obj.type === 'Location';
}

export function isPositionStation(obj: any): obj is PositionStation {
  return isNotNull<PositionStation>(obj) && obj.type === 'Position';
}

export function isValidRoutingRequest(obj: any): obj is RoutingRequestData {
  return isNotNull<RoutingRequestData>(obj) && obj.type === 'Routing';
}

export function isLatLngLiteral(obj: any): obj is LatLngLiteral {
  return (
    isNotNull<LatLngLiteral>(obj) &&
    !isNaN(obj.lat) &&
    isFinite(obj.lat) &&
    !isNaN(obj.lng) &&
    isFinite(obj.lng)
  );
}

export function isLatLngBoundsLiteral(
  obj: any
): obj is ym.provider.LatLngBoundsLiteral {
  return (
    isNotNull<ym.provider.LatLngBoundsLiteral>(obj) &&
    isLatLngLiteral(obj.northEast) &&
    isLatLngLiteral(obj.southWest)
  );
}

export function isLatLngBoundsContext(
  obj: any
): obj is ym.provider.LatLngBoundsContext {
  return (
    isNotNull<ym.provider.LatLngBoundsContext>(obj) &&
    'function' === typeof obj.getNorthEast &&
    'function' === typeof obj.getSouthWest
  );
}

export function isSingleSlotAddress(obj: any): obj is Address {
  return isNotNull<Address>(obj) && isNotEmptyString(obj.singleSlot);
}

export function isAddress(obj: any): obj is Address {
  return (
    isNotNull<Address>(obj) &&
    (isNotEmptyString(obj.city) || isNotEmptyString(obj.zip))
  );
}

export function isSearchEntries(obj: any): obj is SearchEntries {
  return isNotNull<SearchEntries>(obj) && obj.type === 'SearchEntries';
}

export function isResultEntry(obj: any): obj is ResultEntry {
  return isNotNull<ResultEntry>(obj) && obj.type === 'Result';
}

export function isRouteTargetEntry(obj: any): obj is RouteTargetEntry {
  return (
    isNotNull<RouteTargetEntry>(obj) && obj.type === 'Result' && !!obj.referrer
  );
}

export function isRouteEntry(obj: any): obj is RouteEntry {
  return isNotNull<RouteEntry>(obj) && obj.type === 'Route';
}

export function isClusterEntry(obj: any): obj is ClusterEntry {
  return isNotNull<ClusterEntry>(obj) && obj.type === 'Cluster';
}

export function isGeocoderEntry(obj: any): obj is GeocoderEntry {
  return isNotNull<GeocoderEntry>(obj) && obj.type === 'Geocoder';
}

export function isRouteGeocoderEntry(obj: any): obj is RouteGeocoderEntry {
  return isNotNull<RouteGeocoderEntry>(obj) && obj.type === 'RouteGeocoder';
}

export function isOffset(obj: any): obj is Offset {
  return (
    isNotNull<Offset>(obj) &&
    !isNaN(obj.top) &&
    !isNaN(obj.right) &&
    !isNaN(obj.bottom) &&
    !isNaN(obj.left)
  );
}

export function hasUrlParameters(obj: any): obj is UrlParameters {
  return isNotNull<UrlParameters>(obj);
}

export function isWayPoint(obj: any): obj is WayPoint {
  return isNotNull<WayPoint>(obj) && obj.type === 'WayPoint';
}

export function hasWayPoints(obj: any): obj is WayPoint[] {
  return isArray(obj) && obj.length > 0 && isWayPoint(obj[0]);
}
